import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { ExperimentFastTrackGoogleApplication } from '@percent/cause-dashboard/app/experiments/ExperimentFastTrackGoogleApplication'
import { ExperimentSignUpToViewProgress } from '@percent/cause-dashboard/app/experiments/ExperimentSignUpToViewProgress'
import { CentralCardLayoutWrapper, Loader } from '@percent/cause-dashboard/common/components'
import { useQuery } from '@percent/cause-dashboard/common/hooks'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { Alert, Typography } from '@percent/lemonade'
import { useTranslation } from 'react-i18next'
import styles from './AgentVerificationEmailValidation.module.scss'

export const authErrorCodeToLocaleMap = {
  'agent_verification_user_verification/invalid_token': {
    title: 'errorMessage.avFailedInvalidTokenTitle',
    description: 'errorMessage.avFailedInvalidTokenDesc'
  },
  '*': {
    title: 'errorMessage.avFailedOtherTitle',
    description: 'errorMessage.avFailedOtherDesc'
  }
} as const

export const AgentVerificationEmailValidation = () => {
  const { t } = useTranslation()
  const { t: loco, i18n } = useTranslation('causeDashboardLoco')
  const { search } = useLocation()
  const { agentVerificationService } = useServices()
  const agentVerificationToken = new URLSearchParams(search).get('agentVerificationToken') || ''

  const [{ isLoading, data, error }] = useQuery(
    agentVerificationService.getConfirmAgentVerificationRequest,
    agentVerificationToken
  )

  const responseData = data?.data?.data

  useEffect(() => {
    if (data?.data?.data?.language) {
      i18n.changeLanguage(data?.data?.data?.language)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  if (isLoading) {
    return <Loader />
  }

  if (error || !responseData) {
    const errorCode = error?.code || 'request_error'

    const errorObject = Object.keys(authErrorCodeToLocaleMap).includes(errorCode)
      ? authErrorCodeToLocaleMap[error?.code as keyof typeof authErrorCodeToLocaleMap]
      : authErrorCodeToLocaleMap['*']

    return (
      <CentralCardLayoutWrapper heading={t('errorMessage.avFailedPageTitle')} hideLocalePicker>
        <Alert variant={'error'} title={t(errorObject.title)}>
          {t(errorObject.description)}
        </Alert>
      </CentralCardLayoutWrapper>
    )
  }

  return (
    <CentralCardLayoutWrapper
      heading={loco('agent_verification_confirmation.success_page.heading.thank_you')}
      AgentVerificationLogo={data?.data?.data?.logo}
      agentVerificationLink={data?.data?.data?.agentVerificationRedirectUrl}
      alignCenter
      hideLocalePicker
    >
      <Typography variant="h6" className={styles.agentVerificationText}>
        {loco('agent_verification_confirmation.success_page.body.application_being_processed')}
      </Typography>
      {/* These two experiments cannot show at the same time due to partner include/exclude lists */}
      <ExperimentFastTrackGoogleApplication
        organizationId={responseData?.organisationId ?? ''}
        partnerId={responseData?.partnerId ?? ''}
        userLanguage={responseData?.language ?? 'en'}
      />

      <ExperimentSignUpToViewProgress
        organizationId={responseData?.organisationId ?? ''}
        partnerId={responseData?.partnerId ?? ''}
        userLanguage={responseData?.language ?? 'en'}
      />
    </CentralCardLayoutWrapper>
  )
}
